/**
 * ANTLITZWERK collection quick add
 *
 * Preserves Horizon's cart and quick-add-modal logic while giving collection
 * actions their own quiet interaction language.
 *
 * Fine pointers: the approved desktop square -> label behaviour is unchanged.
 * Phones: the first deliberate tap unfolds the compact corner sign into a clear
 * invitation. A second tap performs the native action. Ignored invitations fold
 * back after three seconds or immediately when the visitor scrolls.
 * Larger coarse-pointer devices retain reveal-then-commit.
 */
(() => {
  'use strict';

  const COMPONENT_SELECTOR = '.aw-collection-quick-add';
  const BUTTON_SELECTOR = 'button.quick-add__button';
  const EXPANDED_ATTRIBUTE = 'data-aw-expanded';
  const COMMITTED_ATTRIBUTE = 'data-aw-committed';
  const MOBILE_ENGAGED_ATTRIBUTE = 'data-aw-engaged';
  const MOBILE_SUCCESS_ATTRIBUTE = 'data-aw-success';

  const REVEAL_DURATION = 3000;
  const MOBILE_CHOOSE_DURATION = 1100;
  const MOBILE_PENDING_FALLBACK = 5000;
  const MOBILE_SUCCESS_DURATION = 1650;

  const coarsePointer = window.matchMedia('(hover: none), (pointer: coarse)');
  const phoneLayout = window.matchMedia('(max-width: 749px)');
  const revealTimers = new WeakMap();
  const commitTimers = new WeakMap();
  const mobileTimers = new WeakMap();
  const mobileActionTokens = new WeakMap();
  let mobileActionSequence = 0;

  /** @param {Element | null} component */
  const clearRevealTimer = (component) => {
    if (!component) return;
    const timer = revealTimers.get(component);
    if (timer) window.clearTimeout(timer);
    revealTimers.delete(component);
  };

  /** @param {Element | null} component */
  const clearCommitTimer = (component) => {
    if (!component) return;
    const timer = commitTimers.get(component);
    if (timer) window.clearTimeout(timer);
    commitTimers.delete(component);
  };

  /** @param {Element | null} component */
  const clearMobileTimer = (component) => {
    if (!component) return;
    const timer = mobileTimers.get(component);
    if (timer) window.clearTimeout(timer);
    mobileTimers.delete(component);
  };

  /** @param {Element | null} component */
  const collapse = (component) => {
    if (!component) return;
    clearRevealTimer(component);
    component.removeAttribute(EXPANDED_ATTRIBUTE);
  };

  /** @param {Element | null} except */
  const collapseAll = (except = null) => {
    document.querySelectorAll(`${COMPONENT_SELECTOR}[${EXPANDED_ATTRIBUTE}]`).forEach((component) => {
      if (component !== except) collapse(component);
    });
  };

  /** @param {Element | null} component */
  const resetMobileState = (component) => {
    if (!component) return;
    clearMobileTimer(component);
    component.removeAttribute(MOBILE_ENGAGED_ATTRIBUTE);
    component.removeAttribute(MOBILE_SUCCESS_ATTRIBUTE);
    mobileActionTokens.delete(component);
  };

  /** @param {Element | null} except */
  const resetOtherMobileStates = (except = null) => {
    document
      .querySelectorAll(
        `${COMPONENT_SELECTOR}[${MOBILE_ENGAGED_ATTRIBUTE}], ${COMPONENT_SELECTOR}[${MOBILE_SUCCESS_ATTRIBUTE}]`
      )
      .forEach((component) => {
        if (component !== except) resetMobileState(component);
      });
  };

  /**
   * @param {Element} component
   * @param {number} duration
   */
  const scheduleMobileReset = (component, duration) => {
    clearMobileTimer(component);
    mobileTimers.set(component, window.setTimeout(() => resetMobileState(component), duration));
  };

  /**
   * The first deliberate phone tap only reveals the meaning of the corner sign.
   * It does not submit the form or open the existing variant modal.
   * @param {Element} component
   */
  const revealMobileChoice = (component) => {
    resetOtherMobileStates(component);
    collapse(component);
    clearCommitTimer(component);
    component.removeAttribute(COMMITTED_ATTRIBUTE);
    component.removeAttribute(MOBILE_SUCCESS_ATTRIBUTE);
    component.setAttribute(MOBILE_ENGAGED_ATTRIBUTE, '');
    mobileActionTokens.delete(component);
    scheduleMobileReset(component, REVEAL_DURATION);
  };

  /**
   * The second phone tap is allowed to continue into Horizon's native action.
   * Direct add remains unfolded while its cart promise is pending; choose-options
   * folds back once the existing modal has opened.
   * @param {Element} component
   * @param {HTMLButtonElement} button
   */
  const commitMobileAction = (component, button) => {
    resetOtherMobileStates(component);
    collapse(component);
    clearCommitTimer(component);
    clearMobileTimer(component);
    component.removeAttribute(COMMITTED_ATTRIBUTE);
    component.removeAttribute(MOBILE_SUCCESS_ATTRIBUTE);
    component.setAttribute(MOBILE_ENGAGED_ATTRIBUTE, '');
    mobileActionTokens.set(component, ++mobileActionSequence);

    const isDirectAdd = button.classList.contains('quick-add__button--add');
    scheduleMobileReset(component, isDirectAdd ? MOBILE_PENDING_FALLBACK : MOBILE_CHOOSE_DURATION);
  };

  /** @param {Element | null} component */
  const confirmMobileAction = (component) => {
    if (!component || !phoneLayout.matches) return;
    resetOtherMobileStates(component);
    clearMobileTimer(component);
    component.setAttribute(MOBILE_ENGAGED_ATTRIBUTE, '');
    component.setAttribute(MOBILE_SUCCESS_ATTRIBUTE, '');
    scheduleMobileReset(component, MOBILE_SUCCESS_DURATION);
  };

  /**
   * @param {Element} component
   * @param {HTMLButtonElement} button
   */
  const reveal = (component, button) => {
    collapseAll(component);
    clearCommitTimer(component);
    component.removeAttribute(COMMITTED_ATTRIBUTE);
    component.setAttribute(EXPANDED_ATTRIBUTE, '');

    try {
      button.focus({ preventScroll: true });
    } catch (_error) {
      button.focus();
    }

    clearRevealTimer(component);
    revealTimers.set(component, window.setTimeout(() => collapse(component), REVEAL_DURATION));
  };

  /**
   * Holds the compact state after a committed desktop/tablet action so a
   * lingering hover cannot reopen the label over Horizon's success animation.
   * @param {Element} component
   * @param {HTMLButtonElement} button
   */
  const holdCompactAfterCommit = (component, button) => {
    collapse(component);
    clearCommitTimer(component);
    component.setAttribute(COMMITTED_ATTRIBUTE, '');

    const release = () => {
      clearCommitTimer(component);
      component.removeAttribute(COMMITTED_ATTRIBUTE);
    };

    if (coarsePointer.matches) {
      commitTimers.set(component, window.setTimeout(release, 1200));
      return;
    }

    commitTimers.set(
      component,
      window.setTimeout(() => {
        clearCommitTimer(component);

        if (!button.matches(':hover')) {
          release();
          return;
        }

        button.addEventListener('pointerleave', release, { once: true });
        commitTimers.set(component, window.setTimeout(release, 6000));
      }, 1100)
    );
  };

  /** @param {MouseEvent} event */
  const isDeliberateCoarsePointerClick = (event) => {
    // Keyboard and assistive-technology activation commonly report detail 0;
    // those paths remain single-action for accessibility.
    if (event.detail === 0) return false;

    if ('pointerType' in event && typeof event.pointerType === 'string' && event.pointerType) {
      return event.pointerType === 'touch' || event.pointerType === 'pen';
    }

    return coarsePointer.matches;
  };

  /** @param {Event} event */
  const componentFromEvent = (event) => {
    const target = event.target;
    return target instanceof Element ? target.closest(COMPONENT_SELECTOR) : null;
  };

  // Capture runs before Horizon's declarative document handler. On phones the
  // first pointer click is consumed only to reveal the invitation; the second is
  // left untouched so the existing cart or variant-modal logic performs normally.
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest(`${COMPONENT_SELECTOR} ${BUTTON_SELECTOR}`);
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;

      const component = button.closest(COMPONENT_SELECTOR);
      if (!component) return;

      if (phoneLayout.matches) {
        // Keyboard and assistive-technology activation stays a single action.
        // Every deliberate pointer activation in the phone layout uses the
        // requested reveal -> decide interaction, including desktop emulation.
        if (event.detail !== 0 && !component.hasAttribute(MOBILE_ENGAGED_ATTRIBUTE)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          revealMobileChoice(component);
          return;
        }

        commitMobileAction(component, button);
        return;
      }

      if (isDeliberateCoarsePointerClick(event) && !component.hasAttribute(EXPANDED_ATTRIBUTE)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        reveal(component, button);
        return;
      }

      holdCompactAfterCommit(component, button);
    },
    { capture: true }
  );

  // ProductForm dispatches this before its request and exposes the completed
  // cart result through event.promise. Confirmation therefore represents a
  // genuine successful add rather than merely a pressed button.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (!phoneLayout.matches || event.action !== 'add' || !(event.promise instanceof Promise)) return;

    const component = componentFromEvent(event);
    if (!component || !component.hasAttribute(MOBILE_ENGAGED_ATTRIBUTE)) return;

    const actionToken = mobileActionTokens.get(component);
    if (!actionToken) return;

    event.promise
      .then((result) => {
        if (mobileActionTokens.get(component) !== actionToken) return;

        const didError = result?.detail?.didError === true;
        const hasUserErrors = Array.isArray(result?.userErrors) && result.userErrors.length > 0;

        if (didError || hasUserErrors) {
          resetMobileState(component);
          return;
        }

        confirmMobileAction(component);
      })
      .catch(() => {
        if (mobileActionTokens.get(component) === actionToken) resetMobileState(component);
      });
  });

  document.addEventListener('shopify:cart:error', (event) => {
    const component = componentFromEvent(event);
    if (component) resetMobileState(component);
  });

  window.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const activeComponent = target.closest(COMPONENT_SELECTOR);

      if (phoneLayout.matches) {
        resetOtherMobileStates(activeComponent);
      } else {
        collapseAll(activeComponent);
      }
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      collapseAll();
      resetOtherMobileStates();
    },
    { capture: true }
  );

  window.addEventListener(
    'scroll',
    () => {
      if (phoneLayout.matches) {
        resetOtherMobileStates();
      } else {
        collapseAll();
      }
    },
    { capture: true, passive: true }
  );

  window.addEventListener('pagehide', () => {
    collapseAll();
    resetOtherMobileStates();
  });
})();
